<template>
  <div></div>
</template>
<script>

import { setFSmessage, setFSthread } from "@/services/messages.js";
import * as emailApi from "@/services/email_interface.js";
import { getMember } from "@/services/teams.js";
import { useEmailSettingsStore } from '@/stores/email_settings_store';
import { usePatentStore } from '@/stores/patent_store';
import { useTrademarkStore } from '@/stores/trademark_store';
import { mapStores } from 'pinia';


export default {
  components: {
  },
  data() {
    return {
      userId: null,
      firmId: null,
      processing_stopped: false,
      applicationIds: [],
      patentNumbers: [],
      patentMap: {},
      tmIds: [],
      tmNumbers: [],
      tmMap: {},
      docketNumbers: [],
      docketRegexPatterns: [],
      docketRegexes: [],
      docketMap: {},
      tmDocketNumbers: [],
      tmDocketRegexPatterns: [],
      tmDocketRegexes: [],
      tmDocketMap: {},
      counter: 0,
    };
  },
  computed: {
    emailSettings() {
      return this.emailSettingsStore.settings;
    },
    processingStatus() {
      return this.emailSettingsStore.processing_status;
    },
    ...mapStores(useEmailSettingsStore, usePatentStore, useTrademarkStore),
  },
  async mounted() {
    this.userId = this.$cookies.get("uid");
    this.firmId = this.$cookies.get("defaultFirm");
    this.emailSettingsStore.subscribeToSettings(this.firmId, this.userId);
    //TODO normalize this so every method takes firmId, userId.  NOT userId, firmId
    await this.patentStore.loadPatents(this.userId, this.firmId);
    // trademarks are staff-only (rules) — the trademark store's listeners are
    // denied for client-role members, so skip the load entirely. Clients CAN
    // have assigned patents, so the patent store still loads for them.
    const member = await getMember(this.firmId, this.userId).catch(() => null);
    if (member?.role !== "client") {
      await this.trademarkStore.loadData(this.firmId, this.userId);
    }
  },
  watch: {
    processingStatus(newStatus) {
      if (!emailApi.alreadySignedIn()) {
        return;
      }
      if (newStatus == "started") {
        this.processing_stopped = false;
        this.startSync();
      } else if (newStatus == "stopped") {
        this.processing_stopped = true;
      }
    },
  },
  methods: {
    async startSync() {
      await this.prepMatchingPatterns();
      let query = "";
      if (!this.emailSettingsStore.timeframe) {
        // if no timeframe is set, default to 1 month
        const now = new Date();
        const oneMonthsAgo = new Date();
        oneMonthsAgo.setMonth(now.getMonth() - 1);
        const timestamp = Math.floor(oneMonthsAgo.getTime() / 1000);
        this.emailSettingsStore.setTimeframe(timestamp);
        query = `after:${timestamp}`;
      }
      else {
        query = `after:${this.emailSettingsStore.timeframe}`;
      }
      await this.syncMessages(query);
      this.processing_stopped = true;
      this.emailSettingsStore.stopProcessing();
    },
    generateDocketRegexPatterns(docketNumbers) {
      if (!Array.isArray(docketNumbers))  {
        throw new Error("Invalid input: docketNumbers should be an array.");
      }
      if (docketNumbers.length === 0) {
        return [];
      }
      const escapeRegexChar = (ch) => {
        const special = /[.*+?^${}()|[\]\\]/g;
        return ch.replace(special, '\\$&');
      };

      const toCharType = (ch) => {
        if (/[A-Z]/.test(ch)) return 'A'; // uppercase letter
        if (/[a-z]/.test(ch)) return 'a'; // lowercase letter
        if (/\d/.test(ch)) return '9';    // digit
        return escapeRegexChar(ch);       // special character
      };

      const patterns = [];

      for (const str of docketNumbers) {
        let typePattern = '';
        let i = 0;
        while (i < str.length) {
          const type = toCharType(str[i]);
          let count = 1;
          while (i + count < str.length && toCharType(str[i + count]) === type) {
            count++;
          }
          if (type === 'A') typePattern += `[A-Z]{${count}}`;
          else if (type === 'a') typePattern += `[a-z]{${count}}`;
          else if (type === '9') typePattern += `\\d{${count}}`;
          else typePattern += `${type.repeat(count)}`;
          i += count;
        }
        // if the pattern is not already in the patterns array, add it
        if (!patterns.includes(typePattern)) {
          patterns.push(typePattern);
        }
      }
      return patterns;
    },
      
    async prepMatchingPatterns() {

      // 1. prepare matching patterns related to application ids, patent numbers, and docket numbers
      while (this.patentStore.loaded != true) {
        await new Promise(r => setTimeout(r, 1000));
      }

      this.applicationIds = this.patentStore.all_patents.map(patent => patent.applId);
      // sort the applicationIds in ascending order
      this.applicationIds.sort((a, b) => a - b);

      let isSame = true;
      if (this.emailSettings && this.emailSettings.applicationIds) {
        // compare whether this.applicationIds is the same as this.emailSettings.applicationIds
        // if they are the same, then reuse the existing emailSettings' applicationIds, patentNumbers,
        // patentMap, docketRegexes, and docketMap
        if (this.applicationIds.length == this.emailSettings.applicationIds.length) {
          for (let i = 0; i < this.applicationIds.length; i++) {
            if (this.applicationIds[i] != this.emailSettings.applicationIds[i]) {
              isSame = false;
              break;
            }
          }
          if (isSame) {
            this.patentNumbers = this.emailSettings.patentNumbers;
            this.patentMap = this.emailSettings.patentMap;
            this.docketNumbers = this.emailSettings.docketNumbers;
            this.docketRegexPatterns = this.emailSettings.docketRegexPatterns;
            this.docketRegexes = this.docketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
            this.docketMap = this.emailSettings.docketMap;
          }
        }
      } else {
        isSame = false;
      }
      // else, update the emailSettings with the new applicationIds, new patentNumbers, new docketRegexes, and new docketMap
      if (!isSame) {
        this.patentNumbers = this.patentStore.all_patents.filter(patent => patent.patentNumber).map(patent => patent.patentNumber);
        this.patentMap = {};
        for (const patent of this.patentStore.all_patents) {
          if (patent.patentNumber) {
            this.patentMap[patent.patentNumber] = patent.applId;
          }
        }
        // sort the patentNumbers in ascending order
        this.patentNumbers.sort((a, b) => a - b);

        this.docketNumbers = this.patentStore.all_patents.filter(patent => patent.appAttrDockNumber).map(patent => patent.appAttrDockNumber);
        this.docketMap = {};
        for (const patent of this.patentStore.all_patents) {
          if (patent.appAttrDockNumber) {
            this.docketMap[patent.appAttrDockNumber] = patent.applId;
          }
        }
        this.docketRegexPatterns = this.generateDocketRegexPatterns(this.docketNumbers);
        this.docketRegexes = this.docketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
      }

      // 2. prepare matching patterns related to trademark ids, trademark numbers, and docket numbers
      while (this.trademarkStore.tm_loaded != true) {
        await new Promise(r => setTimeout(r, 1000));
      }

      this.tmIds = this.trademarkStore.the_trademarks.map(trademark => trademark.serialNumber);
      // sort the tmIds in ascending order
      this.tmIds.sort((a, b) => a - b);

      let isTMSame = true;
      if (this.emailSettings && this.emailSettings.tmIds) {
        if (this.tmIds.length == this.emailSettings.tmIds.length) {
          for (let i = 0; i < this.tmIds.length; i++) {
            if (this.tmIds[i] != this.emailSettings.tmIds[i]) {
              isTMSame = false;
              break;
            }
          }
          if (isTMSame) {
            this.tmNumbers = this.emailSettings.tmNumbers;
            this.tmMap = this.emailSettings.tmMap;
            this.tmDocketNumbers = this.emailSettings.tmDocketNumbers;
            this.tmDocketRegexPatterns = this.emailSettings.tmDocketRegexPatterns;
            this.tmDocketRegexes = this.tmDocketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
            this.tmDocketMap = this.emailSettings.tmDocketMap;
          }
        }
      } else {
        isTMSame = false;
      }
      if (!isTMSame) {
        this.tmNumbers = this.trademarkStore.the_trademarks.filter(tm => tm.registrationNumber).map(tm => tm.registrationNumber);
        this.tmMap = {};
        for (const tm of this.trademarkStore.the_trademarks) {
          if (tm.registrationNumber) {
            this.tmMap[tm.registrationNumber] = tm.serialNumber;
          }
        }
        this.tmNumbers.sort((a, b) => a - b);

        this.tmDocketNumbers = this.trademarkStore.the_trademarks.filter(tm => tm.statusObj.status.attrnyDktNumber).map(tm => tm.statusObj.status.attrnyDktNumber);
        this.tmDocketMap = {};
        for (const tm of this.trademarkStore.the_trademarks) {
          if (tm.statusObj.status.attrnyDktNumber) {
            this.tmDocketMap[tm.statusObj.status.attrnyDktNumber] = tm.serialNumber;
          }
        }
        this.tmDocketRegexPatterns = this.generateDocketRegexPatterns(this.tmDocketNumbers);
        this.tmDocketRegexes = this.tmDocketRegexPatterns.map(pattern => new RegExp(`${pattern}`, 'g'));
      }

      this.emailSettingsStore.updateSettings(
        {
          applicationIds: this.applicationIds,
          patentNumbers: this.patentNumbers,
          patentMap: this.patentMap,
          docketNumbers: this.docketNumbers,
          docketRegexPatterns: this.docketRegexPatterns,
          docketMap: this.docketMap,
          tmIds: this.tmIds,
          tmNumbers: this.tmNumbers,
          tmMap: this.tmMap,
          tmDocketNumbers: this.tmDocketNumbers,
          tmDocketRegexPatterns: this.tmDocketRegexPatterns,
          tmDocketMap: this.tmDocketMap,
        }
      );
    },
    async syncMessages(query, lastBatch = null) {
      let batch = lastBatch;
      let hasMoreMessages = true;
      this.emailSettingsStore.resetCounter();

      while (hasMoreMessages) {
        // Load the current batch of messages
        batch = await emailApi.loadBatch(batch, query);
        let messages = batch?.messages || [];
        // check if stopped and return if true
        if (this.processing_stopped) {
          return;
        }

        for (const message of messages) {
          // Check if stopped and return if true
          if (this.processing_stopped) {
            return;
          }

          if (!message || !message.id) {
            continue;
          }

          let categories = await this.processMessage(message);
          this.emailSettingsStore.increaseCounter();
          if (!categories) {
            continue;
          }

          // Ensure idempotent storage
          await this.storeMessage(categories, message.id);
          await this.storeThread(categories, message);
        }

        // Check if there are more messages to load
        hasMoreMessages = emailApi.moreMessages(batch);
      }

      return;
    },
    async storeMessage(categories, messageId) {
      return await setFSmessage(this.firmId, this.userId, messageId, categories);
    },
    async storeThread(categories, message) {
      const threadId = message.threadId
      return await setFSthread(this.firmId, this.userId, threadId, categories);
    },
    removeHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || "";
    },
    async processMessage(message) {
      // note that message now is a detailed message with body, headers, etc.
      const email = message;
      if (!email || !email.id) {
        return null;
      }
      let patentIds = [];
      let trademarkIds = [];
      let hasTrademark = false;
      let hasPatent = false;

      const body = emailApi.getBody(email);
      const subject = emailApi.getSubjectLine(email);
      const textBody = this.removeHtml(body);

      const email_content = textBody + ' ' + subject;

      if (!email_content) {
        return null;
      }

      //TODO this needs to just be the text, not including any tags...
      const numberExp = /[0-9/,]{7,12}/g;

      const idCandidates = email_content.match(numberExp);

      if (idCandidates) {
        const punctRegex = /,|\//g

        for (const patentId of idCandidates) {
          const formattedId = patentId.replaceAll(punctRegex, "");
          if (this.applicationIds.includes(formattedId)) {
            hasPatent = true;
            if (!patentIds.includes(formattedId))
              patentIds.push(formattedId);
          } else if (this.patentNumbers.includes(formattedId)) {
            hasPatent = true;
            if (!patentIds.includes(this.patentMap[formattedId]))
              patentIds.push(this.patentMap[formattedId]);
          }
          else if (this.tmIds.includes(formattedId)) {
            hasTrademark = true;
            if (!trademarkIds.includes(formattedId))
              trademarkIds.push(formattedId);
          } else if (this.tmNumbers.includes(formattedId)) {
            hasTrademark = true;
            if (!trademarkIds.includes(this.tmMap[formattedId]))
              trademarkIds.push(this.tmMap[formattedId]);
          }
        }
      }
      for (const docketRegex of this.docketRegexes) {
        const docketNumberCandidates = email_content.match(docketRegex);
        if (docketNumberCandidates) {
          for (const docketNumber of docketNumberCandidates) {
            if (this.docketNumbers.includes(docketNumber)) { // note, use docketmap to check instead.
              hasPatent = true;
              if (!patentIds.includes(this.docketMap[docketNumber])) {
                patentIds.push(this.docketMap[docketNumber]);
              }
            }
          }
        }
      }

      for (const tmDocketRegex of this.tmDocketRegexes) {
        const docketNumberCandidates = email_content.match(tmDocketRegex);
        if (docketNumberCandidates) {
          for (const docketNumber of docketNumberCandidates) {
            if (this.tmDocketNumbers.includes(docketNumber)) { // note, use docketmap to check instead.
              hasTrademark = true;
              if (!trademarkIds.includes(this.tmDocketMap[docketNumber])) {
                trademarkIds.push(this.tmDocketMap[docketNumber]);
              }
            }
          }
        }
      }

      const platformDate = emailApi.platformDate(email);

      //TODO add unread flag to the record (this is not in the default data...)
      //TODO for threads we need to store the last thread date not the email date...
      //TODO we need to merge the patent ids and client ids incase we aer updating a thread when a new message is sent.
      //TODO we need to add a reason for each categorization also. ie. "matched applId", "matched docket number" etc.
      return {
        processed: new Date().toISOString(),
        date: platformDate,
        patentIds: patentIds,
        hasPatent: hasPatent,
        trademarkIds: trademarkIds,
        hasTrademark: hasTrademark,
        isRelevant: hasPatent || hasTrademark,
      };
    },
  }
}
</script>

